import {
  IS_CHAT_ATTACHMENTS_ENABLED,
  IS_EVIDENCE_DEBUG_PANEL_ENABLED,
  IS_EVIDENCE_USER_DOCS_ENABLED,
} from '@/features/flags/legacy-flags';
import { useMemo, useState } from 'react';

interface ChatDebugPanelProps {
  mode?: string;
  sessionId?: string | null;
  chatId?: string | null;
  isStreaming?: boolean;
  /** Dev-only: show the Experience Feedback nudge (UMUX-Lite) for preview */
  onShowFeedbackNudge?: () => void;
}

/**
 * Debug panel shown when SHOW_DEBUG_PANEL flag is enabled.
 * Displays chat version info and feature flags.
 */
export const ChatDebugPanel = ({
  mode,
  sessionId,
  chatId,
  isStreaming,
  onShowFeedbackNudge,
}: ChatDebugPanelProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowDebugPanel = IS_EVIDENCE_DEBUG_PANEL_ENABLED();

  const flags = useMemo(
    () => ({
      evidenceAutoMode: true,
      sourceControlBadges: true,
      userDocs: IS_EVIDENCE_USER_DOCS_ENABLED() ?? false,
      chatAttachments: IS_CHAT_ATTACHMENTS_ENABLED() ?? false,
      references: true,
    }),
    []
  );

  const evidenceVersion = 3;

  if (!shouldShowDebugPanel || !isOpen) return null;

  return (
    <div className="fixed right-4 top-4 z-50 font-mono text-xs">
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="rounded-full bg-amber-500 px-3 py-1 text-black shadow-lg transition-all hover:bg-amber-400"
        >
          🔧 Debug
        </button>
      ) : (
        <div className="w-64 rounded-lg border border-amber-500/30 bg-white p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between border-b border-amber-500/20 pb-2">
            <span className="font-semibold text-amber-500">Chat Debug Info</span>
            <div className="flex gap-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="rounded px-1.5 text-text-tertiary transition-colors hover:bg-surface-hover hover:text-text-primary"
                title="Minimize"
              >
                −
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded px-1.5 text-text-tertiary transition-colors hover:bg-surface-hover hover:text-text-primary"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="space-y-1.5 text-text-secondary">
            <div className="flex justify-between">
              <span className="text-text-tertiary">Evidence Version:</span>
              <span className="text-text-primary">v{evidenceVersion}</span>
            </div>
            <DebugRow label="Mode" value={mode ?? '-'} />
            <DebugRow
              label="Session ID"
              value={sessionId ? truncate(sessionId, 12) : 'sessionless'}
            />
            <DebugRow label="Chat ID" value={chatId ? truncate(chatId, 12) : '-'} />
            <DebugRow label="Streaming" value={isStreaming ? '🟢 Active' : '⚪ Idle'} />

            {onShowFeedbackNudge && (
              <div className="mt-2 border-t border-amber-500/20 pt-2">
                <button
                  type="button"
                  onClick={onShowFeedbackNudge}
                  className="w-full rounded border border-amber-500/50 bg-amber-500/10 px-2 py-1.5 text-left text-amber-500 transition-colors hover:bg-amber-500/20"
                >
                  Show feedback nudge (preview)
                </button>
              </div>
            )}
            <div className="mt-2 border-t border-amber-500/20 pt-2">
              <div className="mb-1 text-amber-500/80">Feature Flags:</div>
              <FlagRow label="auto-mode" value={flags.evidenceAutoMode} />
              <FlagRow label="source-badges" value={flags.sourceControlBadges} />
              <FlagRow label="user-docs" value={flags.userDocs} />
              <FlagRow label="attachments" value={flags.chatAttachments} />
              <FlagRow label="references" value={flags.references} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DebugRow = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex justify-between">
    <span className="text-text-tertiary">{label}:</span>
    <span className="text-text-primary">{value}</span>
  </div>
);

const FlagRow = ({ label, value }: { label: string; value: boolean }) => (
  <div className="flex justify-between">
    <span className="text-text-tertiary">{label}:</span>
    <span className={value ? 'text-validation-success' : 'text-text-tertiary'}>
      {value ? '✓' : '✗'}
    </span>
  </div>
);

const truncate = (str: string, len: number) =>
  str.length > len ? `${str.slice(0, len)}...` : str;
